import React, { useMemo, useState } from "react";
import { MultipleAnswerValue, MultipleQuestionOptionsOption } from "consumerhouse-shared";
import { Condition, MultipleConditionValue } from "consumerhouse-shared";
import { MultipleQuestionOptions } from "consumerhouse-shared";
import { EditableList } from "../EditableList";
import { FormRow, RawFormCheck, RawFormInput, RawFormMultipleSelect, RawFormRadio, RawFormSelect, SmallInput } from "../FormElements";
import { GroupContainer } from "../GroupContainer";
import { ConditionEditProps, OptionsEditProps, QuestionContainer, QuestionProps, QuestionNote, QuestionDefinition } from "../QuestionAnswer";
import { useLanguage } from "../../useLanguage";
import { faCamera, faForward, faList, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LinkButton } from "../LinkButton";
import { IconButton } from "../../IconButton";
import Button from "../Button";
import { arrayEqual } from "../../util";

export const MultipleQuestionDefinition: QuestionDefinition<MultipleQuestionOptions> = {
    icon: faList,
    defaultOptions: {
        multiple: false,
        options: ["optie 1", "optie 2", "optie 3"],
        other: false,
    },
    questionComponent: MultipleQuestion,
    optionsEditComponent: MultipleOptionsEdit,
    conditionEditComponent: MultipleConditionEdit,
};

function MultipleQuestion(props: QuestionProps<MultipleQuestionOptions, MultipleAnswerValue>) {
    const options = useMemo(() => {
        let o = props.question.optionsJson!;
        if (o.random) {
            // Only randomize non-unique options
            let indicesToRandomize = [];
            for (let i = 0; i < o.options.length; i++) {
                let option = o.options[i];
                if (typeof option !== "string" && !option.unique) {
                    indicesToRandomize.push(i);
                }
            }

            let newOptions = [...o.options];
            for (let i = 0; i < indicesToRandomize.length; i++) {
                let index = indicesToRandomize[i];
                let randomIndex = indicesToRandomize[Math.floor(indicesToRandomize.length * Math.random())];
                let temp = newOptions[index];
                newOptions[index] = newOptions[randomIndex];
                newOptions[randomIndex] = temp;
            }

            return {
                ...o,
                options: newOptions,
            };
        } else {
            return o;
        }
    }, [props.question.optionsJson]);

    const [selected, setSelected] = useState<MultipleAnswerValue>(() =>
        props.value
            ? props.value
            : {
                  // Select all inverted options by default
                  selected: options.options
                      .filter((e) => (typeof e === "string" ? false : !!e.inverted))
                      .map((e) => (typeof e === "string" ? e : e.option)),
                  other: "",
              }
    );

    const Input = options.multiple ? RawFormCheck : RawFormRadio;
    const language = useLanguage();

    function handleChange(checked: boolean, option: MultipleQuestionOptionsOption) {
        let unique = false;
        let optionName: string;
        if (typeof option !== "string") {
            unique = !!option.unique;
            optionName = option.option;
        } else {
            optionName = option;
        }

        let invertedSelected = [...selected.selected];
        options.options.forEach((option) => {
            if (typeof option !== "string" && option.inverted) {
                if (invertedSelected.includes(option.option)) {
                    // Remove option
                    invertedSelected.splice(invertedSelected.indexOf(option.option), 1);
                } else {
                    // Add option
                    invertedSelected.push(option.option);
                }
            }
        });

        let newInvertedSelected: string[];
        if (checked) {
            if (!options.multiple || unique) {
                newInvertedSelected = [optionName];
            } else {
                let uniqueAnswers = options.options
                    .filter((e) => (typeof e === "string" ? false : !!e.unique))
                    .map((e) => (typeof e === "string" ? e : e.option));
                newInvertedSelected = [...invertedSelected.filter((e) => !uniqueAnswers.includes(e)), optionName];
            }
        } else {
            newInvertedSelected = invertedSelected.filter((v) => !Condition.compare(optionName, v));
        }

        let newSelected = [...newInvertedSelected];
        options.options.forEach((option) => {
            if (typeof option !== "string" && option.inverted) {
                if (newSelected.includes(option.option)) {
                    // Remove option
                    newSelected.splice(newSelected.indexOf(option.option), 1);
                } else {
                    // Add option
                    newSelected.push(option.option);
                }
            }
        });

        let newSelectedWithOther = {
            selected: newSelected,
            other: selected.other,
        };
        setSelected(newSelectedWithOther);
        props.onChange(newSelectedWithOther);
    }

    return (
        <>
            {options.multiple && <QuestionNote>{language("question-multiple")}</QuestionNote>}
            <QuestionContainer>
                {options.options.map((option, index) => {
                    let inverted = false;
                    let optionName: string;
                    if (typeof option !== "string") {
                        inverted = !!option.inverted;
                        optionName = option.option;
                    } else {
                        optionName = option;
                    }

                    let checked = selected.selected?.some((v) => Condition.compare(v, optionName)) ?? [];

                    return (
                        <FormRow key={index}>
                            <Input
                                disabled={props.readOnly}
                                checked={inverted ? !checked : checked}
                                name={props.question.parameter!}
                                onChange={(ev) => handleChange(ev.target.checked, option)}
                                value={optionName}>
                                {optionName}
                            </Input>
                        </FormRow>
                    );
                })}

                {options.other && (
                    <FormRow>
                        <Input
                            disabled={props.readOnly}
                            checked={selected.selected.some((e) => e === "other")}
                            name={props.question.parameter!}
                            onChange={(ev) => handleChange(true, "other")}
                            value="other">
                            {language("question-other")}
                        </Input>
                        <div>
                            <RawFormInput
                                style={{ margin: "0 0.5em" }}
                                disabled={props.readOnly}
                                defaultValue={selected.other ?? ""}
                                onClick={() => {
                                    if (!selected.selected.includes("other")) {
                                        setSelected({
                                            ...selected,
                                            selected: [...selected.selected, "other"],
                                        });
                                    }
                                }}
                                onBlur={(ev) => {
                                    let newSelected = { selected: selected.selected, other: ev.target.value };
                                    setSelected(newSelected);
                                    props.onChange(newSelected);
                                }}
                            />
                        </div>
                    </FormRow>
                )}
            </QuestionContainer>
        </>
    );
}

const OptionListItem = styled.li`
    display: flex;
    align-items: center;
    list-style: none;
    padding: 0;
    margin: 0.1em 0;
`;

function MultipleOptionsEdit(props: OptionsEditProps<MultipleQuestionOptions>) {
    const options = props.options ?? { multiple: false, other: false, options: [] };
    return (
        <GroupContainer title="Opties" color="#5a8ee8">
            <ul>
                {options.options.map((option, i) => (
                    <OptionListItem key={i}>
                        <span style={{ color: "#0005", padding: "0 0.5em 0 0", width: "26px" }}>{i + 1}</span>
                        <SmallInput
                            style={{ flexGrow: 1 }}
                            value={typeof option === "string" ? option : option.option}
                            onChange={(ev) => {
                                let arr = [...options.options];
                                arr[i] = ev.target.value;
                                props.onChange({ ...options, options: arr });
                            }}
                        />

                        <RawFormCheck
                            checked={typeof option === "string" ? false : option.inverted}
                            onChange={(ev) => {
                                let inverted = ev.target.checked;
                                let newOption =
                                    typeof option === "string" ? { option: option, inverted: inverted } : { ...option, inverted: inverted };

                                let arr = [...options.options];
                                arr[i] = newOption;
                                props.onChange({ ...options, options: arr });
                            }}>
                            Omgekeerd
                        </RawFormCheck>

                        <RawFormCheck
                            checked={typeof option === "string" ? false : option.unique}
                            onChange={(ev) => {
                                let unique = ev.target.checked;
                                let newOption = typeof option === "string" ? { option: option, unique: unique } : { ...option, unique: unique };

                                let arr = [...options.options];
                                arr[i] = newOption;
                                props.onChange({ ...options, options: arr });
                            }}>
                            Uniek
                        </RawFormCheck>
                        <span style={{ color: "#0005", padding: "0 0.5em" }}>
                            <FontAwesomeIcon icon={faForward} />
                        </span>
                        <RawFormSelect
                            onChange={(ev) => {
                                let newOption = typeof option === "string" ? { option: option } : { ...option };
                                if (ev.target.value === "") {
                                    newOption.jumpToParameter = undefined;
                                } else {
                                    newOption.jumpToParameter = ev.target.value;
                                }

                                let arr = [...options.options];
                                arr[i] = newOption;
                                props.onChange({ ...options, options: arr });
                            }}
                            value={typeof option === "string" || option.jumpToParameter === undefined ? "" : String(option.jumpToParameter)}>
                            <option value="">Volgende vraag</option>
                            <option value="stop">Stop</option>
                            {props.allQuestions.map((question) => (
                                <option key={question.id} value={question.parameter!}>
                                    Naar {question.parameter}
                                </option>
                            ))}
                        </RawFormSelect>
                        {/* <IconButton type="button" onClick={() => {}} style={{ whiteSpace: "nowrap" }}>
                            <FontAwesomeIcon icon={faCamera} /> {}
                        </IconButton> */}
                        <IconButton
                            type="button"
                            onClick={() => {
                                let arr = [...options.options];
                                arr.splice(i, 1);
                                props.onChange({ ...options, options: arr });
                            }}>
                            <FontAwesomeIcon icon={faTrash} />
                        </IconButton>
                    </OptionListItem>
                ))}
            </ul>
            <div style={{ margin: "0.8em 0 0 0", display: "flex", alignItems: "center" }}>
                <Button
                    style={{ margin: "0 0.5em 0 0" }}
                    type="button"
                    icon={faPlus}
                    onClick={() => props.onChange({ ...options, options: [...options.options, ""] })}>
                    Optie toevoegen
                </Button>
                <RawFormCheck
                    checked={options.multiple}
                    onChange={(ev) =>
                        props.onChange({
                            ...options,
                            multiple: ev.target.checked,
                        })
                    }>
                    Meerdere
                </RawFormCheck>
                <RawFormCheck
                    checked={options.other}
                    onChange={(ev) =>
                        props.onChange({
                            ...options,
                            other: ev.target.checked,
                        })
                    }>
                    Anders veld
                </RawFormCheck>
                <RawFormCheck
                    checked={options.random}
                    onChange={(ev) =>
                        props.onChange({
                            ...options,
                            random: ev.target.checked,
                        })
                    }>
                    Willekeurig
                </RawFormCheck>
                <RawFormCheck
                    checked={options.coded || false}
                    onChange={(ev) =>
                        props.onChange({
                            ...options,
                            coded: ev.target.checked,
                        })
                    }>
                    Gecodeerde kolom
                </RawFormCheck>
            </div>
        </GroupContainer>
    );
}

function MultipleConditionEdit(props: ConditionEditProps<MultipleConditionValue, MultipleQuestionOptions>) {
    const value: string[] = props.condition.valueJson?.orOptions ?? [];

    return (
        <div style={{ flexGrow: 1, padding: "0.3em 1.2em 0.3em 0.3em", display: "flex", flexDirection: "column" }}>
            <RawFormMultipleSelect
                multiple
                value={value}
                onChange={(ev) => {
                    props.onChange({ orOptions: Array.from(ev.target.selectedOptions).map((e) => e.value) });
                }}>
                {props.question.optionsJson?.options.map((option, i) => (
                    <option key={i} value={typeof option === "string" ? option : option.option}>
                        {typeof option === "string" ? option : option.option}
                    </option>
                ))}
            </RawFormMultipleSelect>
            <small>De gebruiker moet minstens 1 uit de geselecteerde items kiezen om te voldoen. Gebruik ctrl om meerdere items te selecteren.</small>
        </div>
    );
}
